import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  AlertCircle,
  Shield,
  CheckCircle2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  RefreshCw,
  MoreHorizontal,
  TestTube,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useSortableData } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";
import { automationApi, Credential } from "@/services/api/automation";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/automation/credentials")({
  component: CredentialsPage,
});

interface CredentialFormData {
  credential_type: string;
  service_name: string;
  name: string;
  description?: string;
  credentials: Record<string, unknown>;
}

const CREDENTIAL_TYPES = [
  { value: "api_key", label: "API Key" },
  { value: "oauth", label: "OAuth 2.0" },
  { value: "basic_auth", label: "Basic Authentication" },
  { value: "aws", label: "AWS Credentials" },
  { value: "azure", label: "Azure Credentials" },
  { value: "gcp", label: "Google Cloud Credentials" },
  { value: "database", label: "Database Connection" },
  { value: "webhook", label: "Webhook Secret" },
  { value: "smtp", label: "SMTP Credentials" },
  { value: "custom", label: "Custom" },
];

function CredentialsPage() {
  const { token } = useAuth();
  const { can } = usePermissions();
  const canManageAutomation = can("manage", "automation");
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] =
    useState<Credential | null>(null);

  const [testingCredential, setTestingCredential] = useState<string | null>(
    null,
  );

  // Form state
  const [formData, setFormData] = useState<CredentialFormData>({
    credential_type: "api_key",
    service_name: "",
    name: "",
    description: "",
    credentials: {},
  });

  const [searchTerm, setSearchTerm] = useState("");

  // Sort + paginate
  const { sortedData: sortedCredentials, requestSort, getSortDirection } =
    useSortableData(credentials, { initialSort: { key: "name", direction: "asc" } });

  const {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData({
    data: sortedCredentials as unknown as Record<string, unknown>[],
    itemsPerPage: 25,
    searchFields: ["name", "service_name", "credential_type"],
    searchTerm,
  });
  const displayCredentials = paginatedData as unknown as Credential[];

  useEffect(() => {
    loadCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadCredentials = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await automationApi.credentials.list(token);
      setCredentials(response || []);
    } catch (err) {
      console.error("Failed to load credentials:", err);
      setError("Failed to load credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCredential = async () => {
    if (!token) return;
    setError(null);
    try {
      await automationApi.credentials.create(token, formData);
      await loadCredentials();
      setIsAddDialogOpen(false);
      resetForm();
      toast.success("Credential created successfully");
    } catch (err) {
      console.error("Failed to create credential:", err);
      setError("Failed to create credential");
    }
  };

  const handleUpdateCredential = async () => {
    if (!token || !selectedCredential) return;

    setError(null);
    try {
      await automationApi.credentials.update(
        token,
        selectedCredential.id,
        formData,
      );
      await loadCredentials();
      setIsEditDialogOpen(false);
      setSelectedCredential(null);
      resetForm();
      toast.success("Credential updated successfully");
    } catch (err) {
      console.error("Failed to update credential:", err);
      setError("Failed to update credential");
    }
  };

  const handleDeleteCredential = async (credentialId: string) => {
    if (!token) return;
    if (!confirm("Are you sure you want to delete this credential?")) return;

    setError(null);
    try {
      await automationApi.credentials.delete(token, credentialId);
      await loadCredentials();
      toast.success("Credential deleted");
    } catch (err) {
      console.error("Failed to delete credential:", err);
      setError("Failed to delete credential");
    }
  };

  const handleTestCredential = async (credentialId: string) => {
    if (!token) return;
    setTestingCredential(credentialId);
    setError(null);
    try {
      const response = await automationApi.credentials.test(
        token,
        credentialId,
      );
      if (response.success) {
        toast.success("Credential test passed");
        setTimeout(() => setTestingCredential(null), 2000);
      } else {
        toast.error("Credential test failed", {
          description: response.message,
        });
        setTestingCredential(null);
      }
    } catch (err) {
      console.error("Failed to test credential:", err);
      toast.error("Failed to test credential");
      setTestingCredential(null);
    }
  };

  const resetForm = () => {
    setFormData({
      credential_type: "api_key",
      service_name: "",
      name: "",
      description: "",
      credentials: {},
    });
  };

  const openEditDialog = (credential: Credential) => {
    setSelectedCredential(credential);
    setFormData({
      credential_type: credential.credential_type,
      service_name: credential.service_name,
      name: credential.name,
      description: credential.description,
      credentials: {}, // Don't populate sensitive data
    });
    setIsEditDialogOpen(true);
  };

  const renderCredentialFields = () => {
    switch (formData.credential_type) {
      case "api_key":
        return (
          <div className="space-y-2">
            <Label htmlFor="api-key">API Key</Label>
            <Input
              id="api-key"
              type="password"
              value={(formData.credentials.api_key as string) || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  credentials: {
                    ...formData.credentials,
                    api_key: e.target.value,
                  },
                })
              }
              placeholder="Enter API key"
            />
          </div>
        );

      case "basic_auth":
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={(formData.credentials.username as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      username: e.target.value,
                    },
                  })
                }
                placeholder="Enter username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={(formData.credentials.password as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      password: e.target.value,
                    },
                  })
                }
                placeholder="Enter password"
              />
            </div>
          </>
        );

      case "oauth":
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="client-id">Client ID</Label>
              <Input
                id="client-id"
                value={(formData.credentials.client_id as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      client_id: e.target.value,
                    },
                  })
                }
                placeholder="Enter client ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-secret">Client Secret</Label>
              <Input
                id="client-secret"
                type="password"
                value={(formData.credentials.client_secret as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      client_secret: e.target.value,
                    },
                  })
                }
                placeholder="Enter client secret"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="token-url">Token URL (optional)</Label>
              <Input
                id="token-url"
                value={(formData.credentials.token_url as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      token_url: e.target.value,
                    },
                  })
                }
                placeholder="https://api.example.com/oauth/token"
              />
            </div>
          </>
        );

      case "aws":
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="access-key">Access Key ID</Label>
              <Input
                id="access-key"
                value={(formData.credentials.access_key_id as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      access_key_id: e.target.value,
                    },
                  })
                }
                placeholder="Enter AWS access key ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secret-key">Secret Access Key</Label>
              <Input
                id="secret-key"
                type="password"
                value={(formData.credentials.secret_access_key as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      secret_access_key: e.target.value,
                    },
                  })
                }
                placeholder="Enter AWS secret access key"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Region (optional)</Label>
              <Input
                id="region"
                value={(formData.credentials.region as string) || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentials: {
                      ...formData.credentials,
                      region: e.target.value,
                    },
                  })
                }
                placeholder="us-east-1"
              />
            </div>
          </>
        );

      default:
        return (
          <div className="space-y-2">
            <Label htmlFor="custom-json">Credentials (JSON)</Label>
            <textarea
              id="custom-json"
              className="w-full min-h-[100px] p-2 border rounded-md font-mono text-sm"
              value={JSON.stringify(formData.credentials, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setFormData({ ...formData, credentials: parsed });
                } catch {
                  // Invalid JSON, ignore
                }
              }}
              placeholder='{\n  "key": "value"\n}'
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Credentials</h1>
          <p className="mt-2 text-lg text-slate-600">
            Manage secure credentials for external services and integrations
          </p>
        </div>
        {canManageAutomation && (
          <Button className="rounded-full" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Credential
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search credentials..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={loadCredentials} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayCredentials.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No credentials match your search" : "No credentials configured"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "Try a different search term." : "Add credentials to securely connect to external services."}
            </p>
            {!searchTerm && canManageAutomation && (
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Credential
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead direction={getSortDirection("name")} onSort={() => requestSort("name")}>Name</SortableTableHead>
                <SortableTableHead direction={getSortDirection("credential_type")} onSort={() => requestSort("credential_type")}>Type</SortableTableHead>
                <SortableTableHead direction={getSortDirection("service_name")} onSort={() => requestSort("service_name")}>Service</SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled>Description</SortableTableHead>
                <SortableTableHead direction={getSortDirection("created_at")} onSort={() => requestSort("created_at")}>Created</SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayCredentials.map((credential) => (
                <TableRow key={credential.id}>
                  <TableCell className="font-medium">{credential.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {CREDENTIAL_TYPES.find((t) => t.value === credential.credential_type)?.label || credential.credential_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{credential.service_name}</TableCell>
                  <TableCell className="max-w-[250px]">
                    <span className="text-sm text-muted-foreground line-clamp-1">
                      {credential.description || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {new Date(credential.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleTestCredential(credential.id)}>
                          {testingCredential === credential.id ? (
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                          ) : (
                            <TestTube className="h-4 w-4 mr-2" />
                          )}
                          Test
                        </DropdownMenuItem>
                        {canManageAutomation && (
                          <>
                            <DropdownMenuItem onClick={() => openEditDialog(credential)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDeleteCredential(credential.id)} className="text-destructive focus:text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={totalItems}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={currentPage > 1}
              canGoNext={currentPage < totalPages}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
              onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            />
          </div>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Credential</DialogTitle>
            <DialogDescription>
              Store secure credentials for service integrations
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Credential Type</Label>
                <Select
                  value={formData.credential_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, credential_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDENTIAL_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="service">Service Name</Label>
                <Input
                  id="service"
                  value={formData.service_name}
                  onChange={(e) =>
                    setFormData({ ...formData, service_name: e.target.value })
                  }
                  placeholder="e.g., stripe, sendgrid, aws-s3"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Credential Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Production API Key"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Brief description of this credential"
              />
            </div>

            {renderCredentialFields()}

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateCredential}>
                Create Credential
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Credential</DialogTitle>
            <DialogDescription>
              Update credential information (leave password fields empty to keep
              existing values)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Credential Type</Label>
                <Input value={formData.credential_type} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-service">Service Name</Label>
                <Input
                  id="edit-service"
                  value={formData.service_name}
                  onChange={(e) =>
                    setFormData({ ...formData, service_name: e.target.value })
                  }
                  placeholder="e.g., stripe, sendgrid, aws-s3"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-name">Credential Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Production API Key"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description (optional)</Label>
              <Input
                id="edit-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Brief description of this credential"
              />
            </div>

            {renderCredentialFields()}

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateCredential}>
                Update Credential
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
